import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import axios from "axios";
import { toast } from "react-toastify";
import { getConfigWithToken } from "../../config/config";
import formatDate from "../../utils/formatDate";

const TEAL = "#0D9488";
const TEAL_DARK = "#0F766E";
const ISSUE_RED = "#DC2626";

const ISSUE_TYPE_OPTIONS = [
  "More Info Needed",
  "Invalid Information",
  "Inactive Coverage",
  "Not Accepted",
  "Authorization Required",
  "Referral Required",
  "Could Not Verify",
  "Other",
];

const emptyFormSnapshot = {
  verificationMode: "active",
  coverageEffectiveDate: "",
  coverageEndDate: "",
  hasRemainingDeductible: false,
  remainingDeductibleBalance: "",
  estimatedCopay: "",
  estimatedCoinsurance: "",
  issueType: "",
  internalNote: "",
};

const parseDollarAmount = (value) => {
  if (!value || !String(value).trim()) return null;
  const normalized = String(value).replace(/[$,\s]/g, "");
  if (!/^\d+(\.\d{1,2})?$/.test(normalized)) return null;
  return normalized;
};

const parsePercentage = (value) => {
  if (!value || !String(value).trim()) return null;
  const normalized = String(value).replace(/[%\s]/g, "");
  if (!/^\d+(\.\d{1,2})?$/.test(normalized)) return null;
  const numeric = Number(normalized);
  if (numeric < 0 || numeric > 100) return null;
  return normalized;
};

const maskMemberId = (memberId) => {
  if (!memberId || memberId === "N/A") return memberId;
  const value = String(memberId);
  if (value.length <= 4) return value;
  return `${value.slice(0, 3)}${"*".repeat(Math.max(value.length - 6, 1))}${value.slice(-3)}`;
};

const validateActiveForm = ({
  coverageEffectiveDate,
  coverageEndDate,
  hasRemainingDeductible,
  remainingDeductibleBalance,
  estimatedCopay,
  estimatedCoinsurance,
}) => {
  const errors = {};

  if (!coverageEffectiveDate?.trim()) {
    errors.coverageEffectiveDate = "Coverage effective date is required.";
  }

  if (
    coverageEndDate &&
    coverageEffectiveDate &&
    coverageEndDate < coverageEffectiveDate
  ) {
    errors.coverageEndDate =
      "Coverage end date cannot be before the effective date.";
  }

  if (hasRemainingDeductible) {
    if (!remainingDeductibleBalance?.trim()) {
      errors.remainingDeductibleBalance =
        "Remaining deductible balance is required when deductible applies.";
    } else if (parseDollarAmount(remainingDeductibleBalance) === null) {
      errors.remainingDeductibleBalance = "Enter a valid dollar amount.";
    }
  }

  if (estimatedCopay?.trim() && parseDollarAmount(estimatedCopay) === null) {
    errors.estimatedCopay = "Enter a valid dollar amount.";
  }

  if (
    estimatedCoinsurance?.trim() &&
    parsePercentage(estimatedCoinsurance) === null
  ) {
    errors.estimatedCoinsurance = "Enter a valid percentage.";
  }

  return errors;
};

const validateIssueForm = ({ issueType, internalNote }) => {
  const errors = {};

  if (!issueType?.trim()) {
    errors.issueType = "Select an issue type before notifying the patient.";
  }

  if (issueType === "Other" && !internalNote?.trim()) {
    errors.internalNote = "Add a note explaining the issue.";
  }

  return errors;
};

const InfoField = ({ label, value }) => (
  <div>
    <div style={{ fontSize: "12px", color: "#6B7280", marginBottom: "2px" }}>
      {label}
    </div>
    <div style={{ fontSize: "14px", fontWeight: 600, color: "#111827" }}>
      {value || "N/A"}
    </div>
  </div>
);

const VerifyInsuranceModal = ({
  show,
  onHide,
  session,
  userToken,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [verificationMode, setVerificationMode] = useState("active");
  const [insuranceDetails, setInsuranceDetails] = useState(null);
  const initialSnapshotRef = useRef(emptyFormSnapshot);

  const [coverageEffectiveDate, setCoverageEffectiveDate] = useState("");
  const [coverageEndDate, setCoverageEndDate] = useState("");
  const [hasRemainingDeductible, setHasRemainingDeductible] = useState(false);
  const [remainingDeductibleBalance, setRemainingDeductibleBalance] =
    useState("");
  const [estimatedCopay, setEstimatedCopay] = useState("");
  const [estimatedCoinsurance, setEstimatedCoinsurance] = useState("");
  const [issueType, setIssueType] = useState("");
  const [internalNote, setInternalNote] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const appointmentId = session?._id || session?.id;

  const currentSnapshot = useMemo(
    () => ({
      verificationMode,
      coverageEffectiveDate,
      coverageEndDate,
      hasRemainingDeductible,
      remainingDeductibleBalance,
      estimatedCopay,
      estimatedCoinsurance,
      issueType,
      internalNote,
    }),
    [
      verificationMode,
      coverageEffectiveDate,
      coverageEndDate,
      hasRemainingDeductible,
      remainingDeductibleBalance,
      estimatedCopay,
      estimatedCoinsurance,
      issueType,
      internalNote,
    ],
  );

  const isDirty = useMemo(
    () =>
      JSON.stringify(currentSnapshot) !==
      JSON.stringify(initialSnapshotRef.current),
    [currentSnapshot],
  );

  const resetFormState = () => {
    setVerificationMode("active");
    setShowMore(false);
    setShowDiscardConfirm(false);
    setCoverageEffectiveDate("");
    setCoverageEndDate("");
    setHasRemainingDeductible(false);
    setRemainingDeductibleBalance("");
    setEstimatedCopay("");
    setEstimatedCoinsurance("");
    setIssueType("");
    setInternalNote("");
    setFieldErrors({});
    setInsuranceDetails(null);
    initialSnapshotRef.current = { ...emptyFormSnapshot };
  };

  useEffect(() => {
    if (!show || !appointmentId) return;

    const fetchDetails = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/payments/insurance-details/${appointmentId}`,
          getConfigWithToken(userToken),
        );

        if (response.data.success) {
          const data = response.data.data;
          setInsuranceDetails(data);

          const meta = data.verificationMeta || {};
          const toDateInputValue = (value) => {
            if (!value) return "";
            const raw = String(value);
            if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);

            const date = new Date(value);
            if (Number.isNaN(date.getTime())) return "";

            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, "0");
            const day = String(date.getDate()).padStart(2, "0");
            return `${year}-${month}-${day}`;
          };
          const nextMode = meta.status === "invalid" ? "issue" : "active";
          const nextState = {
            verificationMode: nextMode,
            coverageEffectiveDate: toDateInputValue(meta.coverageEffectiveDate),
            coverageEndDate: toDateInputValue(meta.coverageEndDate),
            hasRemainingDeductible: Boolean(meta.hasRemainingDeductible),
            remainingDeductibleBalance: meta.remainingDeductibleBalance || "",
            estimatedCopay: meta.estimatedCopay || "",
            estimatedCoinsurance: meta.estimatedCoinsurance || "",
            issueType: meta.issueType || "",
            internalNote: meta.internalNote || "",
          };

          setVerificationMode(nextState.verificationMode);
          setCoverageEffectiveDate(nextState.coverageEffectiveDate);
          setCoverageEndDate(nextState.coverageEndDate);
          setHasRemainingDeductible(nextState.hasRemainingDeductible);
          setRemainingDeductibleBalance(nextState.remainingDeductibleBalance);
          setEstimatedCopay(nextState.estimatedCopay);
          setEstimatedCoinsurance(nextState.estimatedCoinsurance);
          setIssueType(nextState.issueType);
          setInternalNote(nextState.internalNote);
          initialSnapshotRef.current = nextState;
        } else {
          toast.error(
            response.data.message || "Failed to load insurance details.",
          );
        }
      } catch (error) {
        console.error("Error loading insurance details:", error);
        toast.error(
          error.response?.data?.message || "Failed to load insurance details.",
        );
      } finally {
        setLoading(false);
      }
    };

    setShowMore(false);
    setShowDiscardConfirm(false);
    fetchDetails();
  }, [show, appointmentId, userToken]);

  useEffect(() => {
    if (!show) {
      resetFormState();
    }
  }, [show]);

  const closeModal = () => {
    setShowDiscardConfirm(false);
    onHide();
  };

  const requestClose = () => {
    if (submitting) return;
    if (isDirty) {
      setShowDiscardConfirm(true);
      return;
    }
    closeModal();
  };

  const handleVerify = async () => {
    const errors = validateActiveForm({
      coverageEffectiveDate,
      coverageEndDate,
      hasRemainingDeductible,
      remainingDeductibleBalance,
      estimatedCopay,
      estimatedCoinsurance,
    });

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      const firstError = Object.values(errors)[0];
      toast.error(firstError);
      return;
    }

    setFieldErrors({});

    try {
      setSubmitting(true);
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/payments/verify-insurance/${appointmentId}`,
        {
          coverageEffectiveDate,
          coverageEndDate: coverageEndDate || null,
          hasRemainingDeductible,
          remainingDeductibleBalance: hasRemainingDeductible
            ? parseDollarAmount(remainingDeductibleBalance)
            : null,
          estimatedCopay: parseDollarAmount(estimatedCopay),
          estimatedCoinsurance: parsePercentage(estimatedCoinsurance),
        },
        getConfigWithToken(userToken),
      );

      if (response.data.success) {
        toast.success("Insurance verified successfully.");
        initialSnapshotRef.current = { ...currentSnapshot };
        onSuccess?.(response.data.data);
        closeModal();
      } else {
        toast.error(response.data.message || "Failed to verify insurance.");
      }
    } catch (error) {
      console.error("Error verifying insurance:", error);
      toast.error(
        error.response?.data?.message || "Failed to verify insurance.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleInvalidate = async () => {
    const errors = validateIssueForm({ issueType, internalNote });

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      toast.error(Object.values(errors)[0]);
      return;
    }

    setFieldErrors({});

    try {
      setSubmitting(true);
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/payments/invalidate-insurance/${appointmentId}`,
        {
          issueType,
          internalNote: internalNote.trim(),
        },
        getConfigWithToken(userToken),
      );

      if (response.data.success) {
        toast.success("Insurance issue flagged and patient notified.");
        initialSnapshotRef.current = { ...currentSnapshot };
        onSuccess?.(response.data.data);
        closeModal();
      } else {
        toast.error(
          response.data.message ||
            "Unable to flag insurance issue. Please try again.",
        );
      }
    } catch (error) {
      console.error("Error invalidating insurance:", error);
      toast.error(
        error.response?.data?.message ||
          "Unable to flag insurance issue. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const formatDisplayDate = (value) => {
    if (!value) return "N/A";
    return formatDate(value, "date");
  };

  const fieldLabelStyle = {
    fontSize: "13px",
    fontWeight: 500,
    color: "#374151",
    marginBottom: "6px",
  };

  const inputStyle = {
    borderRadius: "8px",
    border: "1px solid #D1D5DB",
    padding: "10px 12px",
    fontSize: "14px",
  };

  const inputErrorStyle = {
    ...inputStyle,
    borderColor: ISSUE_RED,
  };

  const FieldError = ({ message }) =>
    message ? (
      <div style={{ color: ISSUE_RED, fontSize: "12px", marginTop: "4px" }}>
        {message}
      </div>
    ) : null;

  return (
    <>
      <Modal
        show={show}
        onHide={requestClose}
        centered
        size="lg"
        backdrop="static"
        className="verify-insurance-modal-root"
        backdropClassName="verify-insurance-modal-backdrop"
      >
        <style>{`
          .verify-insurance-modal-root { z-index: 10050 !important; }
          .verify-insurance-modal-backdrop { z-index: 10049 !important; }
          .verify-insurance-modal-root .modal-content {
            border-radius: 12px;
            border: none;
            box-shadow: 0 20px 40px rgba(0, 0, 0, 0.18);
          }
          .verify-insurance-switch .form-check-input {
            width: 2.5rem;
            height: 1.35rem;
            cursor: pointer;
          }
          .verify-insurance-switch .form-check-input:checked {
            background-color: ${TEAL};
            border-color: ${TEAL};
          }
          .verify-insurance-switch .form-check-input:focus {
            box-shadow: 0 0 0 0.2rem rgba(13, 148, 136, 0.25);
          }
        `}</style>

        <Modal.Header
          style={{
            borderBottom: "1px solid #E5E7EB",
            padding: "20px 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Modal.Title
            style={{
              fontSize: "20px",
              fontWeight: 700,
              color: "#111827",
              margin: 0,
            }}
          >
            Verify Insurance
          </Modal.Title>
          <button
            type="button"
            aria-label="Close"
            onClick={requestClose}
            disabled={submitting}
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              border: "1px solid #E5E7EB",
              backgroundColor: "#FFFFFF",
              color: "#6B7280",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: submitting ? "not-allowed" : "pointer",
              fontSize: "18px",
              lineHeight: 1,
              padding: 0,
            }}
          >
            ×
          </button>
        </Modal.Header>

        <Modal.Body style={{ padding: "24px" }}>
          {loading ? (
            <div
              style={{ textAlign: "center", padding: "40px", color: "#6B7280" }}
            >
              Loading insurance details...
            </div>
          ) : (
            <>
              <div
                style={{
                  border: "1px solid #E5E7EB",
                  borderRadius: "10px",
                  padding: "16px",
                  marginBottom: "20px",
                  backgroundColor: "#FFFFFF",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "14px",
                  }}
                >
                  <h5
                    style={{
                      margin: 0,
                      fontSize: "14px",
                      fontWeight: 600,
                      color: TEAL_DARK,
                    }}
                  >
                    Patient Insurance Info
                  </h5>
                  <button
                    type="button"
                    onClick={() => setShowMore((prev) => !prev)}
                    style={{
                      border: "none",
                      background: "none",
                      color: TEAL,
                      fontSize: "13px",
                      fontWeight: 600,
                      cursor: "pointer",
                      padding: 0,
                    }}
                  >
                    {showMore ? "Hide" : "Show More"}
                  </button>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "14px 20px",
                  }}
                >
                  <InfoField
                    label="Patient Name"
                    value={insuranceDetails?.patientName}
                  />
                  <InfoField
                    label="Insurance Plan"
                    value={insuranceDetails?.insurancePlan}
                  />
                  <InfoField
                    label="Member ID"
                    value={maskMemberId(insuranceDetails?.memberId)}
                  />

                  {showMore && (
                    <>
                      <InfoField
                        label="Date of Birth"
                        value={formatDisplayDate(insuranceDetails?.dateOfBirth)}
                      />
                      <InfoField
                        label="Relationship"
                        value={insuranceDetails?.relationship}
                      />
                      <InfoField
                        label="Sex on Insurance"
                        value={insuranceDetails?.sexOnInsurance}
                      />
                      <div style={{ gridColumn: "1 / -1" }}>
                        <InfoField
                          label="Address"
                          value={insuranceDetails?.address}
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div
                style={{ display: "flex", gap: "12px", marginBottom: "20px" }}
              >
                <button
                  type="button"
                  onClick={() => {
                    setVerificationMode("active");
                    setFieldErrors({});
                  }}
                  style={{
                    flex: 1,
                    border:
                      verificationMode === "active"
                        ? `2px solid ${TEAL}`
                        : "1px solid #D1D5DB",
                    backgroundColor:
                      verificationMode === "active" ? TEAL : "#FFFFFF",
                    borderRadius: "10px",
                    padding: "14px 16px",
                    fontWeight: 600,
                    fontSize: "14px",
                    color:
                      verificationMode === "active" ? "#FFFFFF" : "#6B7280",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                >
                  <span style={{ fontSize: "16px" }}>✓</span>
                  Insurance Active
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setVerificationMode("issue");
                    setFieldErrors({});
                  }}
                  style={{
                    flex: 1,
                    border:
                      verificationMode === "issue"
                        ? `2px solid ${ISSUE_RED}`
                        : "1px solid #D1D5DB",
                    backgroundColor:
                      verificationMode === "issue" ? ISSUE_RED : "#FFFFFF",
                    borderRadius: "10px",
                    padding: "14px 16px",
                    fontWeight: 600,
                    fontSize: "14px",
                    color: verificationMode === "issue" ? "#FFFFFF" : "#6B7280",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                >
                  <span
                    style={{
                      width: "18px",
                      height: "18px",
                      borderRadius: "50%",
                      border: `1.5px solid ${
                        verificationMode === "issue" ? "#FFFFFF" : "#9CA3AF"
                      }`,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "11px",
                      lineHeight: 1,
                      color:
                        verificationMode === "issue" ? "#FFFFFF" : "#9CA3AF",
                    }}
                  >
                    ×
                  </span>
                  Issue Found
                </button>
              </div>

              {verificationMode === "active" ? (
                <div style={{ display: "grid", gap: "16px" }}>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "16px",
                    }}
                  >
                    <Form.Group>
                      <Form.Label style={fieldLabelStyle}>
                        Coverage Effective Date{" "}
                        <span style={{ color: ISSUE_RED }}>*</span>
                      </Form.Label>
                      <Form.Control
                        type="date"
                        value={coverageEffectiveDate}
                        onChange={(e) => {
                          setCoverageEffectiveDate(e.target.value);
                          setFieldErrors((prev) => ({
                            ...prev,
                            coverageEffectiveDate: undefined,
                          }));
                        }}
                        style={
                          fieldErrors.coverageEffectiveDate
                            ? inputErrorStyle
                            : inputStyle
                        }
                        isInvalid={Boolean(fieldErrors.coverageEffectiveDate)}
                      />
                      <FieldError message={fieldErrors.coverageEffectiveDate} />
                    </Form.Group>

                    <Form.Group>
                      <Form.Label style={fieldLabelStyle}>
                        Coverage End Date
                      </Form.Label>
                      <Form.Control
                        type="date"
                        value={coverageEndDate}
                        onChange={(e) => {
                          setCoverageEndDate(e.target.value);
                          setFieldErrors((prev) => ({
                            ...prev,
                            coverageEndDate: undefined,
                          }));
                        }}
                        style={
                          fieldErrors.coverageEndDate
                            ? inputErrorStyle
                            : inputStyle
                        }
                        isInvalid={Boolean(fieldErrors.coverageEndDate)}
                      />
                      <FieldError message={fieldErrors.coverageEndDate} />
                    </Form.Group>
                  </div>

                  <div
                    className="verify-insurance-switch"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "4px 0",
                    }}
                  >
                    <span style={{ ...fieldLabelStyle, marginBottom: 0 }}>
                      Has Remaining Deductible?
                    </span>
                    <Form.Check
                      type="switch"
                      id="hasRemainingDeductible"
                      checked={hasRemainingDeductible}
                      onChange={(e) => {
                        const enabled = e.target.checked;
                        setHasRemainingDeductible(enabled);
                        if (!enabled) {
                          setRemainingDeductibleBalance("");
                          setFieldErrors((prev) => ({
                            ...prev,
                            remainingDeductibleBalance: undefined,
                          }));
                        }
                      }}
                      label=""
                    />
                  </div>

                  {hasRemainingDeductible && (
                    <Form.Group>
                      <Form.Label style={fieldLabelStyle}>
                        Remaining Deductible Balance{" "}
                        <span style={{ color: ISSUE_RED }}>*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="$ 0.00"
                        value={remainingDeductibleBalance}
                        onChange={(e) => {
                          setRemainingDeductibleBalance(e.target.value);
                          setFieldErrors((prev) => ({
                            ...prev,
                            remainingDeductibleBalance: undefined,
                          }));
                        }}
                        style={
                          fieldErrors.remainingDeductibleBalance
                            ? inputErrorStyle
                            : inputStyle
                        }
                        isInvalid={Boolean(fieldErrors.remainingDeductibleBalance)}
                      />
                      <FieldError
                        message={fieldErrors.remainingDeductibleBalance}
                      />
                    </Form.Group>
                  )}

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "16px",
                    }}
                  >
                    <Form.Group>
                      <Form.Label style={fieldLabelStyle}>
                        Estimated Copay
                      </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="$ per session"
                        value={estimatedCopay}
                        onChange={(e) => {
                          setEstimatedCopay(e.target.value);
                          setFieldErrors((prev) => ({
                            ...prev,
                            estimatedCopay: undefined,
                          }));
                        }}
                        style={
                          fieldErrors.estimatedCopay
                            ? inputErrorStyle
                            : inputStyle
                        }
                        isInvalid={Boolean(fieldErrors.estimatedCopay)}
                      />
                      <FieldError message={fieldErrors.estimatedCopay} />
                    </Form.Group>
                    <Form.Group>
                      <Form.Label style={fieldLabelStyle}>
                        Estimated Coinsurance
                      </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="% per session"
                        value={estimatedCoinsurance}
                        onChange={(e) => {
                          setEstimatedCoinsurance(e.target.value);
                          setFieldErrors((prev) => ({
                            ...prev,
                            estimatedCoinsurance: undefined,
                          }));
                        }}
                        style={
                          fieldErrors.estimatedCoinsurance
                            ? inputErrorStyle
                            : inputStyle
                        }
                        isInvalid={Boolean(fieldErrors.estimatedCoinsurance)}
                      />
                      <FieldError message={fieldErrors.estimatedCoinsurance} />
                    </Form.Group>
                  </div>
                </div>
              ) : (
                <div style={{ display: "grid", gap: "16px" }}>
                  <Form.Group>
                    <Form.Label style={fieldLabelStyle}>
                      Issue Type <span style={{ color: ISSUE_RED }}>*</span>
                    </Form.Label>
                    <Form.Select
                      value={issueType}
                      onChange={(e) => {
                        setIssueType(e.target.value);
                        setFieldErrors((prev) => ({
                          ...prev,
                          issueType: undefined,
                          internalNote:
                            e.target.value === "Other"
                              ? prev.internalNote
                              : undefined,
                        }));
                      }}
                      style={
                        fieldErrors.issueType ? inputErrorStyle : inputStyle
                      }
                      isInvalid={Boolean(fieldErrors.issueType)}
                    >
                      <option value="">Select issue type...</option>
                      {ISSUE_TYPE_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </Form.Select>
                    <FieldError message={fieldErrors.issueType} />
                  </Form.Group>

                  <Form.Group>
                    <Form.Label style={fieldLabelStyle}>
                      Internal Note
                      {issueType === "Other" && (
                        <span style={{ color: ISSUE_RED }}> *</span>
                      )}
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      placeholder="Add internal note..."
                      value={internalNote}
                      onChange={(e) => {
                        setInternalNote(e.target.value);
                        setFieldErrors((prev) => ({
                          ...prev,
                          internalNote: undefined,
                        }));
                      }}
                      style={{
                        ...(fieldErrors.internalNote
                          ? inputErrorStyle
                          : inputStyle),
                        resize: "vertical",
                      }}
                      isInvalid={Boolean(fieldErrors.internalNote)}
                    />
                    <FieldError message={fieldErrors.internalNote} />
                  </Form.Group>

                  <div
                    style={{
                      backgroundColor: "#FFFBEB",
                      border: "1px solid #FDE68A",
                      borderRadius: "8px",
                      padding: "12px 14px",
                      fontSize: "13px",
                      color: "#92400E",
                      lineHeight: 1.5,
                      display: "flex",
                      gap: "10px",
                      alignItems: "flex-start",
                    }}
                  >
                    <span
                      style={{
                        width: "18px",
                        height: "18px",
                        borderRadius: "50%",
                        backgroundColor: "#0D9488",
                        color: "#FFFFFF",
                        fontSize: "11px",
                        fontWeight: 700,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        marginTop: "1px",
                      }}
                    >
                      i
                    </span>
                    <span>
                      The patient will be notified and the session payment
                      status will be marked invalid.
                    </span>
                  </div>
                </div>
              )}
            </>
          )}
        </Modal.Body>

        <Modal.Footer
          style={{
            borderTop: "1px solid #E5E7EB",
            padding: "16px 24px",
            display: "block",
          }}
        >
          {verificationMode === "active" ? (
            <Button
              onClick={handleVerify}
              disabled={submitting || loading}
              style={{
                width: "100%",
                backgroundColor: TEAL,
                border: "none",
                borderRadius: "10px",
                padding: "12px 16px",
                fontWeight: 600,
                fontSize: "15px",
              }}
            >
              {submitting ? "Verifying..." : "Confirm & Verify"}
            </Button>
          ) : (
            <Button
              onClick={handleInvalidate}
              disabled={submitting || loading}
              style={{
                width: "100%",
                backgroundColor: ISSUE_RED,
                border: "none",
                borderRadius: "10px",
                padding: "12px 16px",
                fontWeight: 600,
                fontSize: "15px",
              }}
            >
              {submitting ? "Submitting..." : "Flag Issue & Notify Patient"}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      <Modal
        show={showDiscardConfirm}
        onHide={() => setShowDiscardConfirm(false)}
        centered
        backdrop="static"
        className="verify-insurance-discard-root"
        backdropClassName="verify-insurance-discard-backdrop"
      >
        <style>{`
          .verify-insurance-discard-root { z-index: 10060 !important; }
          .verify-insurance-discard-backdrop { z-index: 10059 !important; }
        `}</style>
        <Modal.Header closeButton style={{ borderBottom: "1px solid #E5E7EB" }}>
          <Modal.Title style={{ fontSize: "18px", fontWeight: 600 }}>
            Discard changes?
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ color: "#4B5563", fontSize: "14px" }}>
          You have unsaved changes. If you leave now, your edits will be lost.
        </Modal.Body>
        <Modal.Footer style={{ borderTop: "1px solid #E5E7EB" }}>
          <Button
            variant="light"
            onClick={() => setShowDiscardConfirm(false)}
            style={{ border: "1px solid #D1D5DB" }}
          >
            Keep Editing
          </Button>
          <Button
            onClick={closeModal}
            style={{
              backgroundColor: ISSUE_RED,
              border: "none",
            }}
          >
            Discard
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default VerifyInsuranceModal;
